import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "next-intl";
import { IconX } from "@tabler/icons-react";
import { useSuppressCoach } from "@/lib/onboarding/useSuppressCoach";
import { useEventTimezone } from "@/providers/EventProvider";
import { LoadingSpinner } from "@/components/LoadingSpinner/LoadingSpinner";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";
import { PSlottingAppointment } from "@/components/slotting/PSlottingAppointment";
import { PSlottingCreateRequest } from "@/components/slotting/PSlottingCreateRequest";
import { PSlottingEditRequest } from "@/components/slotting/PSlottingEditRequest";
import { formatDateInTz } from "@/lib/utils/datetime-tz";
import {
  getOfferings,
  getLanes,
  getTreatments,
  getRequestByGuestInOffering,
} from "@/lib/slotting/api";
import type { Offering, AppointmentRequest } from "@/lib/slotting/types";
import type { SlottingModalContext } from "@/lib/slotting/modalContext";
import type { Guest } from "@/types/Guest";

/** Above the GuestDetailLauncher z ladder (max z-[98]), below the AI rail. */
const FLOW_Z = "z-[100]";

type Kind = "appointment" | "request";

type Props = {
  eventId: string;
  kind: Kind;
  guest: { guestId: string; firstName?: string; lastName?: string };
  onClose: () => void;
  onCreated: (kind: Kind) => void;
};

type Step =
  | { phase: "loadingOfferings" }
  | { phase: "noOfferings" }
  | { phase: "loadFailed" }
  | { phase: "pick"; offerings: Offering[] }
  | { phase: "loadingOffering"; offerings: Offering[] }
  | { phase: "noLanes"; offerings: Offering[] }
  | { phase: "ready"; context: SlottingModalContext };

/**
 * Orchestrates creating an appointment / appointment request for a FIXED
 * guest from the guest-detail popup. The create endpoints are offering-scoped,
 * so this picks an offering first (auto-skipped when there is exactly one),
 * loads that offering's lanes/treatments into a SlottingModalContext, and then
 * mounts the board's create modals decoupled from the board store.
 */
export function GuestSlottingCreateFlow({ eventId, kind, guest, onClose, onCreated }: Props) {
  const eventTz = useEventTimezone();

  const [step, setStep] = useState<Step>({ phase: "loadingOfferings" });
  // Set when the guest already has a request in the picked offering (BE allows
  // one per (offering, guest), archived included) — swap create for edit, like
  // the board's silent handoff.
  const [editing, setEditing] = useState<AppointmentRequest | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const offerings = await getOfferings(eventId);
        if (cancelled) return;
        if (offerings.length === 0) {
          setStep({ phase: "noOfferings" });
        } else if (offerings.length === 1) {
          selectOffering(offerings[0], offerings);
        } else {
          setStep({ phase: "pick", offerings });
        }
      } catch {
        if (!cancelled) setStep({ phase: "loadFailed" });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const selectOffering = async (offering: Offering, offerings: Offering[]) => {
    setStep({ phase: "loadingOffering", offerings });
    try {
      const [lanes, treatments, existing] = await Promise.all([
        getLanes(eventId, offering.offeringId),
        getTreatments(eventId, offering.offeringId),
        kind === "request"
          ? getRequestByGuestInOffering(eventId, offering.offeringId, guest.guestId)
          : Promise.resolve(null),
      ]);
      if (kind === "appointment" && lanes.length === 0) {
        setStep({ phase: "noLanes", offerings });
        return;
      }

      const timezone = offering.timezone ?? eventTz ?? "UTC";
      // Seed date = today as a wall-day in the offering tz, clamped into the
      // offering's window so the modal never opens on an unbookable day.
      let currentDate = formatDateInTz(new Date().toISOString(), "en-CA", timezone);
      const minDate = offering.startDt ? formatDateInTz(offering.startDt, "en-CA", timezone) : null;
      const maxDate = offering.endDt ? formatDateInTz(offering.endDt, "en-CA", timezone) : null;
      if (minDate && currentDate < minDate) currentDate = minDate;
      if (maxDate && currentDate > maxDate) currentDate = maxDate;

      const context: SlottingModalContext = { offering, lanes, treatments, timezone, currentDate };
      if (existing) setEditing(existing);
      setStep({ phase: "ready", context });
    } catch {
      setStep({ phase: "loadFailed" });
    }
  };

  if (step.phase === "ready") {
    if (kind === "request" && editing) {
      return (
        <PSlottingEditRequest
          eventId={eventId}
          request={editing}
          context={step.context}
          zClass={FLOW_Z}
          onClose={onClose}
          onSaved={() => onCreated("request")}
        />
      );
    }
    if (kind === "request") {
      return (
        <PSlottingCreateRequest
          eventId={eventId}
          context={step.context}
          zClass={FLOW_Z}
          initialGuest={guest as Guest}
          lockGuest
          onClose={onClose}
          onCreated={() => onCreated("request")}
          onEditExisting={setEditing}
        />
      );
    }
    return (
      <PSlottingAppointment
        mode="create"
        eventId={eventId}
        initialGuestId={guest.guestId}
        initialDate={step.context.currentDate}
        context={step.context}
        zClass={FLOW_Z}
        onClose={onClose}
        onCreated={() => onCreated("appointment")}
      />
    );
  }

  return <FlowShell step={step} kind={kind} onClose={onClose} onPick={selectOffering} />;
}

function FlowShell({
  step,
  kind,
  onClose,
  onPick,
}: {
  step: Exclude<Step, { phase: "ready" }>;
  kind: Kind;
  onClose: () => void;
  onPick: (offering: Offering, offerings: Offering[]) => void;
}) {
  const t = useTranslations("guestDetails");
  const tErrors = useTranslations("errors");
  const tSlotting = useTranslations("slotting");
  useSuppressCoach(); // hide the bottom-right coach so it can't cover this dialog
  const title =
    kind === "appointment" ? t("newAppointmentAction") : t("newRequestAction");
  const closeLabel = tSlotting("modalClose");

  return createPortal(
    <div
      className={`fixed inset-0 ${FLOW_Z} flex items-center justify-center px-4 bg-black/50 sm:pr-[var(--ai-rail-push)] transition-[padding] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="surface-primary rounded-2xl shadow-2xl w-full max-w-sm max-h-[80dvh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4">
          <h2 className="h3 font-primary">{title}</h2>
          <button onClick={onClose} className="font-tertiary hover:font-primary transition-colors">
            <IconX size={20} />
          </button>
        </div>

        <div className="px-6 pb-5 overflow-y-auto min-h-0">
          {step.phase === "loadingOfferings" || step.phase === "loadingOffering" ? (
            <div className="py-6 flex justify-center">
              <LoadingSpinner />
            </div>
          ) : step.phase === "noOfferings" ? (
            <HintBody message={t("noOfferingsHint")} onClose={onClose} closeLabel={closeLabel} />
          ) : step.phase === "loadFailed" ? (
            <HintBody message={tErrors("unknown")} onClose={onClose} closeLabel={closeLabel} />
          ) : step.phase === "noLanes" ? (
            <div className="flex flex-col gap-4">
              <p className="body-3-regular font-secondary">{t("noLanesInOfferingHint")}</p>
              {step.offerings.length > 1 ? (
                <OfferingList offerings={step.offerings} onPick={onPick} label={t("selectOfferingTitle")} />
              ) : (
                <SecondaryButton text={closeLabel} onClick={onClose} width="w-full" />
              )}
            </div>
          ) : (
            <OfferingList offerings={step.offerings} onPick={onPick} label={t("selectOfferingTitle")} />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function HintBody({ message, onClose, closeLabel }: { message: string; onClose: () => void; closeLabel: string }) {
  return (
    <div className="flex flex-col gap-4">
      <p className="body-3-regular font-secondary">{message}</p>
      <SecondaryButton text={closeLabel} onClick={onClose} width="w-full" />
    </div>
  );
}

function OfferingList({
  offerings,
  onPick,
  label,
}: {
  offerings: Offering[];
  onPick: (offering: Offering, offerings: Offering[]) => void;
  label: string;
}) {
  return (
    <div>
      <p className="body-2-medium font-secondary mb-2">{label}</p>
      <div className="flex flex-col gap-1.5">
        {offerings.map((o) => (
          <button
            key={o.offeringId}
            onClick={() => onPick(o, offerings)}
            className="w-full text-left px-3 py-2.5 rounded-lg border outline-tertiary body-3-regular font-primary hover:surface-secondary transition-colors"
          >
            {o.name}
          </button>
        ))}
      </div>
    </div>
  );
}
